import { SCHEDULE_EVERY_VALUES } from "@nova-ai/shared"

import { cadenceLabel, scheduleEveryLabel } from "../../lib/scheduled-job-display"
import { useScheduleEditForm } from "../../hooks/use-schedule-edit-form"
import type { ScheduledJob } from "../../services/use-scheduled-jobs"
import { Combobox } from "../ui/combobox"
import { SegmentedControl } from "../ui/segmented-control"
import { Switch } from "../ui/switch"
import { ScheduleSlots } from "./schedule-slots"

const MODE_OPTIONS = [
  { value: "basic", label: "Simple interval" },
  { value: "advanced", label: "Cron expression" },
] as const
const CADENCE_OPTIONS = SCHEDULE_EVERY_VALUES.map((value) => ({
  value,
  label: scheduleEveryLabel(value),
}))
const ZONES = [
  "Europe/Bratislava",
  "Europe/Prague",
  "Europe/Budapest",
  "Europe/Berlin",
  "Europe/Warsaw",
  "UTC",
]

export function ScheduleEditForm({ job, canEdit }: { job: ScheduledJob; canEdit: boolean }) {
  const form = useScheduleEditForm(job, canEdit)
  const disabled = !canEdit || form.updateMutation.isPending || job.manualOnly
  return (
    <>
      {!canEdit ? (
        <p className="preview-note mb-4">
          Only admins can change cadence, timezone, or enabled state.
        </p>
      ) : null}

      {job.manualOnly ? (
        <p className="preview-note mb-4">
          This job is manual only. Its schedule cannot be resumed. Use Run now to start it.
        </p>
      ) : null}

      <div className="sched-grid">
        <form
          className="card form-card"
          onSubmit={(event) => {
            event.preventDefault()
            form.saveDraft()
          }}
          noValidate
        >
          <h2 className="card-h">Schedule</h2>

          <p className="hint mt-1">
            Changes apply from the next slot. Every save is recorded in change history.
          </p>

          {form.conflict ? (
            <div className="banner" role="alert">
              <div>
                <strong>This schedule changed in another session</strong>

                <p>
                  Your draft is preserved. Reload version {form.conflict.current.schedule.version}{" "}
                  before saving.
                </p>

                <button type="button" className="btn" onClick={form.reloadConflict}>
                  Reload latest
                </button>
              </div>
            </div>
          ) : null}

          <div className="od-stack">
            <div className="field">
              <span className="text-[13px] font-medium">Repeat</span>

              <SegmentedControl
                className="seg self-start"
                label="Repeat"
                options={MODE_OPTIONS}
                value={form.mode}
                onChange={form.setMode}
                disabled={disabled}
              />
            </div>

            {form.mode === "basic" ? (
              <div className="field">
                <label htmlFor="schedule-cadence">Interval</label>

                <Combobox
                  id="schedule-cadence"
                  aria-label="Interval"
                  className="input schedule-interval"
                  options={CADENCE_OPTIONS}
                  value={form.basicCadence ?? undefined}
                  onChange={(value) => form.setBasicCadence(value ?? "")}
                  disabled={disabled}
                />
              </div>
            ) : (
              <div className="field">
                <label htmlFor="schedule-cron">Cron expression</label>

                <input
                  id="schedule-cron"
                  className="input mono"
                  value={form.cronExpression}
                  onChange={(event) => form.setCronExpression(event.target.value)}
                  onBlur={() => form.blur("cronExpression")}
                  disabled={disabled}
                  aria-invalid={Boolean(form.fieldErrors.cronExpression)}
                  aria-describedby="cron-hint cron-error"
                  autoComplete="off"
                  spellCheck={false}
                />

                <span className="err" id="cron-error">
                  {form.fieldErrors.cronExpression}
                </span>

                <span className="hint" id="cron-hint">
                  <span>Five fields: minute, hour, day, month, weekday. Reads as </span>

                  <strong>{cadenceLabel(form.cronExpression)}</strong>

                  <span>.</span>
                </span>
              </div>
            )}

            <div className="field">
              <label htmlFor="schedule-timezone">Timezone</label>

              <input
                id="schedule-timezone"
                className="input"
                list="schedule-zones"
                value={form.timeZone}
                onChange={(event) => form.setTimeZone(event.target.value)}
                onBlur={() => form.blur("timeZone")}
                disabled={disabled}
                aria-invalid={Boolean(form.fieldErrors.timeZone)}
                aria-describedby="timezone-hint timezone-error"
                autoComplete="off"
                spellCheck={false}
              />

              <datalist id="schedule-zones">
                {ZONES.map((zone) => (
                  <option key={zone} value={zone} />
                ))}
              </datalist>

              <span className="err" id="timezone-error">
                {form.fieldErrors.timeZone}
              </span>

              <span className="hint" id="timezone-hint">
                IANA name, for example Europe/Bratislava.
              </span>
            </div>

            <div className="switch-row">
              <div className="od-field">
                <label htmlFor="schedule-enabled" className="font-medium">
                  Active
                </label>

                <span className="hint">Paused jobs keep their schedule but don’t run.</span>
              </div>

              <Switch
                className="switch"
                id="schedule-enabled"
                label="Schedule active"
                checked={form.enabled}
                onCheckedChange={form.setEnabled}
                disabled={disabled}
              />
            </div>
          </div>

          {form.updateMutation.isError && !form.conflict ? (
            <p role="alert" className="err mt-4">
              {form.updateMutation.error.message}
            </p>
          ) : null}

          <SaveControls form={form} disabled={disabled} />
        </form>

        <UpcomingRuns form={form} manualOnly={job.manualOnly} />
      </div>
    </>
  )
}

type ScheduleForm = ReturnType<typeof useScheduleEditForm>
function SaveControls({ form, disabled }: { form: ScheduleForm; disabled: boolean }) {
  const state = form.isDirty ? "Unsaved changes" : "No changes"
  return (
    <div className="save-bar">
      <span className={form.isDirty ? "state dirty" : "state"}>
        {form.updateMutation.isPending ? "Saving…" : state}
      </span>

      <button
        type="button"
        className="btn ghost"
        onClick={form.discard}
        disabled={!form.isDirty || disabled}
      >
        Discard
      </button>

      <button type="submit" className="btn primary" disabled={!form.canSave}>
        {form.updateMutation.isPending ? "Saving…" : "Save schedule"}
      </button>
    </div>
  )
}
function UpcomingRuns({ form, manualOnly }: { form: ScheduleForm; manualOnly: boolean }) {
  return (
    <aside className="card preview-card" aria-labelledby="upcoming-title" aria-live="polite">
      <h2 className="card-h" id="upcoming-title">
        Upcoming runs
      </h2>

      {manualOnly ? (
        <p className="preview-note">Manual only — no scheduled upcoming work.</p>
      ) : (
        <PreviewContent form={form} />
      )}
    </aside>
  )
}
function PreviewContent({ form }: { form: ScheduleForm }) {
  return form.preview ? (
    <>
      <p className="preview-note mt-1">
        {`${cadenceLabel(form.preview.schedule.cron)} · ${form.preview.schedule.timeZone}`}

        {form.isDirty ? <span className="c-info"> · draft</span> : null}
      </p>

      <ScheduleSlots
        slots={form.preview.nextSlots}
        timeZone={form.preview.schedule.timeZone}
        isEnabled={form.enabled}
      />
    </>
  ) : (
    <p className="preview-note">
      {form.previewLoading
        ? "Updating upcoming runs…"
        : `Fix the cron expression or timezone to see upcoming runs.${form.previewError ? ` ${form.previewError}` : ""}`}
    </p>
  )
}
